import { useState, useRef, useEffect } from "react";
import { API } from "../App";
import styles from "./VoicePanel.module.css";

export default function VoicePanel({ showToast }) {

  const [recording, setRecording] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("Click the mic to start speaking");
  const [messages, setMessages] = useState([]);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, []);

  // START RECORDING
  const startRecording = async () => {
    try {

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      chunksRef.current = [];

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: "audio/webm"
      });

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {

        const blob = new Blob(chunksRef.current, { type: "audio/webm" });

        stream.getTracks().forEach((t) => t.stop());

        await processVoice(blob);
      };

      mediaRecorder.start();

      mediaRecorderRef.current = mediaRecorder;

      setRecording(true);
      setStatus("Recording... click again to stop");

    } catch (err) {
      showToast("Microphone access denied", "error");
    }
  };

  // STOP RECORDING
  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
    setStatus("Processing voice...");
  };

  const toggleRecording = () => {
    recording ? stopRecording() : startRecording();
  };

  // MAIN VOICE FLOW
  const processVoice = async (blob) => {

    setLoading(true);

    try {

      const fd = new FormData();
      fd.append("file", blob, "audio.webm");

      // STEP 1 — TRANSCRIBE
      const transcribeRes = await fetch(`${API}/transcribe`, {
        method: "POST",
        body: fd
      });

      if (!transcribeRes.ok) throw new Error();

      const transcribeData = await transcribeRes.json();

      const text = transcribeData.text;

      if (!text) throw new Error();

      setMessages(prev => [...prev, { role: "user", text }]);

      // STEP 2 — SEND TEXT TO AI
      const aiRes = await fetch(`${API}/voice`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded"
        },
        body: new URLSearchParams({
          text: text
        })
      });

      if (!aiRes.ok) throw new Error();

      const aiData = await aiRes.json();

      setMessages(prev => [
        ...prev,
        { role: "ai", text: aiData.response }
      ]);

      setStatus("Done! Click mic to speak again");

    } catch (err) {

      showToast("Voice request failed", "error");
      setStatus("Error. Try again.");

    } finally {
      setLoading(false);
    }
  };

  // MANUAL TEXT INPUT
  const analyzeText = async (text) => {

    const t = text.trim();
    if (!t) return;

    setMessages(prev => [...prev, { role: "user", text: t }]);

    setLoading(true);

    try {

      const res = await fetch(`${API}/health`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ question: t })
      });

      if (!res.ok) throw new Error();

      const data = await res.json();

      setMessages(prev => [
        ...prev,
        { role: "ai", text: data.response }
      ]);

    } catch {

      showToast("Server error", "error");

    } finally {

      setLoading(false);
    }
  };

  const clearChat = () => {
    setMessages([]);
    setStatus("Click the mic to start speaking");
  };

  return (
    <div className={styles.panel}>

      <div className={styles.header}>
        <div>
          <h2>Voice Input</h2>
          <p>Speak your symptoms — AI will respond</p>
        </div>

        {messages.length > 0 && (
          <button onClick={clearChat} className={styles.clearBtn}>
            Clear Chat
          </button>
        )}
      </div>

      {messages.length > 0 && (
        <div className={styles.chatBox}>

          {messages.map((msg, i) => (

            <div
              key={i}
              className={msg.role === "user" ? styles.userMsg : styles.aiMsg}
            >

              <div className={styles.msgLabel}>
                {msg.role === "user" ? "YOU" : "AIDORA"}
              </div>

              <div className={styles.msgText}>
                {msg.text}
              </div>

            </div>
          ))}

          {loading && (
            <div className={styles.aiMsg}>
              <div className={styles.msgLabel}>AIDORA</div>
              <div className={styles.typing}>
                <span />
                <span />
                <span />
              </div>
            </div>
          )}

          <div ref={bottomRef} />

        </div>
      )}

      <div className={styles.micArea}>

        <button
          className={`${styles.micBtn} ${recording ? styles.recording : ""}`}
          onClick={toggleRecording}
          disabled={loading}
        >

          {recording ? "Stop" : "🎤"}

        </button>

      </div>

      <p className={styles.status}>{status}</p>

      <div className={styles.row}>

        <input
          type="text"
          placeholder="Or type symptoms..."
          className={styles.textInput}
          disabled={loading || recording}
          onKeyDown={(e) => {

            if (e.key === "Enter" && e.target.value.trim()) {

              analyzeText(e.target.value);
              e.target.value = "";

            }

          }}
        />

        <button
          className={styles.btnPrimary}
          disabled={loading || recording}
          onClick={(e) => {

            const input = e.target.parentElement.querySelector("input");

            if (input.value.trim()) {

              analyzeText(input.value);
              input.value = "";

            }

          }}
        >
          Send
        </button>

      </div>

    </div>
  );
}